import Phaser from "phaser";
import { Encoder, Decoder } from "@colyseus/schema";
import { SnapshotInterpolation } from "@geckos.io/snapshot-interpolation";

import { MyRoomState, PlayerState } from "../../../server/src/schemas/RoomState";
import type { GameRoomLike } from "../../../server/src/scenes/ServerGameScene";
import type { NewswireMessage } from "../../../server/src/schemas/RoomState";
import { OfflineGameScene } from "./OfflineGameScene";
import { pickRandomOfflineMap } from "./maps";

// The map schema carries a large flat tile array, so give the encoder plenty of
// room (the real server does the same in PhaserServer).
Encoder.BUFFER_SIZE = 512 * 1024;

// Fields mirrored from a ServerTank's schema into its PlayerState wrapper each
// tick (same set the online GameRoom.fixedTick copies).
const TANK_FIELDS = [
  "id", "name", "x", "y", "heading", "speed", "health", "ammo", "team",
  "targetHeading", "up", "fire", "tick", "pillboxCount", "wood",
  "isRespawning", "respawnTimer",
] as const;

type MessageHandler = (data: any) => void;

/**
 * A drop-in stand-in for a Colyseus client `Room` that runs the authoritative
 * game simulation entirely in the browser (no server, no network). It boots a
 * separate headless Phaser.Game running the real `ServerGameScene` logic, and
 * bridges its state to the client via an in-process Colyseus Encoder -> Decoder
 * loop, so `getStateCallbacks(this)` and all schema callbacks work unchanged.
 */
export class LocalRoom {
  // --- Client-facing Room API (what ClientGameScene consumes) ---
  readonly sessionId = "local";
  // getStateCallbacks(room) reads room['serializer'].decoder — provide it.
  serializer: { decoder: Decoder<MyRoomState> };
  get state(): MyRoomState { return this.clientState; }

  // --- Simulation internals ---
  private playerName: string;
  private mapName: string;
  private game: Phaser.Game;
  private scene: OfflineGameScene;

  private simState = new MyRoomState();      // mutated by the simulation
  private clientState = new MyRoomState();    // decoded into; read by the client
  private encoder = new Encoder(this.simState);
  private decoder = new Decoder(this.clientState);
  private si = new SnapshotInterpolation();

  private messageHandlers = new Map<string, MessageHandler>();
  private ready = false;
  private readyResolve!: () => void;

  // The subset of the room the ServerGameScene depends on (GameRoomLike),
  // wired to the simulation state and the local message bus.
  private simRoom: GameRoomLike;

  constructor(options: { playerName?: string } = {}) {
    this.playerName = options.playerName || "Guest";
    this.mapName = pickRandomOfflineMap();
    this.serializer = { decoder: this.decoder };

    this.simRoom = {
      state: this.simState,
      SI: this.si,
      broadcast: (type: string, message?: any) => this.dispatch(type, message),
      broadcastNewswire: (message: NewswireMessage) => this.dispatch("newswire", message),
      sendToClient: (_sessionId: string, type: string, message?: any) => this.dispatch(type, message),
    };
  }

  // Boot the simulation and resolve once the world (map/stations/pillboxes) and
  // the local player exist and the initial state has been synced to the client.
  init(): Promise<void> {
    return new Promise((resolve) => {
      this.readyResolve = resolve;
      this.bootSimulation();
    });
  }

  private bootSimulation(): void {
    const config: Phaser.Types.Core.GameConfig = {
      type: Phaser.HEADLESS,
      fps: { target: 60, forceSetTimeOut: true, smoothStep: false },
      physics: { default: "matter", matter: { gravity: { x: 0, y: 0 }, debug: false } },
      autoFocus: false,
      audio: { noAudio: true },
      banner: false,
    };
    this.game = new Phaser.Game(config);

    const key = "offline-sim";
    const scene = new OfflineGameScene({
      key,
      active: true,
      visible: false,
      physics: { matter: { gravity: { x: 0, y: 0 }, debug: false } } as any,
    });
    // Wire the scene to the simulation before it boots (preload/create run on add).
    scene.room = this.simRoom as any;
    scene.localRoom = this;
    scene.selectedMapName = this.mapName;
    this.scene = scene;

    this.game.scene.add(key, scene, true);
  }

  // Called by OfflineGameScene after its create() finishes building the world.
  handleSceneCreated(): void {
    this.spawnLocalPlayer();

    // Initial full sync so the client-facing state has the map, stations,
    // pillboxes and player before the client registers its callbacks.
    const bytes = this.encoder.encodeAll();
    this.decoder.decode(bytes);
    this.encoder.discardChanges();

    this.ready = true;
    this.readyResolve();
  }

  private spawnLocalPlayer(): void {
    const team = Math.random() < 0.5 ? 1 : 2;
    const spawnPos = this.scene.getSpawnPositionForTeam(team);
    const tank = this.scene.addPlayer(this.sessionId, spawnPos.x, spawnPos.y, this.playerName);
    tank.team = team;
    tank.updateSchema();

    const playerState = new PlayerState();
    this.copyTankSchema(tank.schema, playerState.tank);
    playerState.tank.team = team;
    this.simState.players.set(this.sessionId, playerState);

    console.log(`[offline] local player spawned on team ${team} at (${spawnPos.x}, ${spawnPos.y})`);
  }

  // Runs once per simulation frame (from OfflineGameScene.update): mirror tank
  // schemas into their PlayerState wrappers, then encode the delta and decode it
  // into the client-facing state (which fires the schema callbacks).
  flush(): void {
    if (!this.ready) return;

    this.simState.players.forEach((playerState, sessionId) => {
      const tank = this.scene.players.get(sessionId);
      if (tank) {
        this.copyTankSchema(tank.schema, playerState.tank);
      }
    });

    if (this.encoder.hasChanges) {
      const it = { offset: 0 };
      const bytes = this.encoder.encode(it);
      this.decoder.decode(bytes);
      this.encoder.discardChanges();
    }
  }

  private copyTankSchema(src: any, dst: any): void {
    for (const key of TANK_FIELDS) {
      if (dst[key] !== src[key]) dst[key] = src[key];
    }
  }

  // --- Client-facing message API ---

  onMessage(type: string, callback: MessageHandler): void {
    this.messageHandlers.set(type, callback);
  }

  send(type: string, payload?: any): void {
    switch (type) {
      case "input":
        this.scene.handlePlayerInput(this.sessionId, payload);
        break;
      case "buildTile":
        this.scene.handleBuildTile(this.sessionId, payload);
        break;
      case "placePillbox":
        this.scene.handlePlacePillbox(this.sessionId, payload);
        break;
      case "chatMessage":
        this.handleChat(payload);
        break;
      default:
        console.warn(`[offline] unhandled client message: ${type}`);
    }
  }

  private handleChat(data: { message: string, isAllChat: boolean }): void {
    const playerState = this.simState.players.get(this.sessionId);
    const team = playerState ? playerState.tank.team : 0;
    const sanitized = data.message.replace(/</g, "&lt;").replace(/>/g, "&gt;");
    this.dispatch("newswire", {
      type: "chat",
      playerId: this.sessionId,
      playerName: this.playerName,
      team,
      message: sanitized,
      isTeamChat: !data.isAllChat,
    });
  }

  private dispatch(type: string, data?: any): void {
    const handler = this.messageHandlers.get(type);
    if (handler) handler(data);
  }
}
